import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { getUsers, updateUser, updateHealerVisibility, deleteUser, getUserHealingRequests, impersonateUser } from '../utils/api';
import { useWindowSize } from '../utils/useWindowSize';
import { FaWhatsapp } from 'react-icons/fa';
import { FiTrash2 } from 'react-icons/fi';

const Users = () => {
  const { isMobile } = useWindowSize();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editForm, setEditForm] = useState({ role: '', isActive: true, showHealerNameToPatient: false });
  const [profileModal, setProfileModal] = useState(false);
  const [viewProfileUser, setViewProfileUser] = useState(null);
  const [profileRequests, setProfileRequests] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchUsers = async () => {
    try {
      const params = {};
      if (filter !== 'all') params.role = filter;
      if (search) params.search = search;
      const res = await getUsers(params);
      setUsers(res.data.users);
    } catch (error) {
      console.log('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditForm({ role: user.role, isActive: user.isActive, phone: user.phone || '', showHealerNameToPatient: user.showHealerNameToPatient || false, allowSessionView: user.allowSessionView || false, profileNotes: user.profileNotes || '', maxActivePatients: user.maxActivePatients || '' });
    setShowModal(true);
  };

  var handleViewProfile = async function(user) {
    setViewProfileUser(user);
    setProfileModal(true);
    setProfileRequests([]);
    setProfileLoading(true);
    try {
      var res = await getUserHealingRequests(user._id);
      setProfileRequests(res.data.requests || []);
    } catch (err) {
      setProfileRequests([]);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      var updateData = { role: editForm.role, isActive: editForm.isActive, phone: editForm.phone, profileNotes: editForm.profileNotes };
      if (selectedUser.role === 'healer' || editForm.role === 'healer') {
        updateData.allowSessionView = editForm.allowSessionView;
        updateData.maxActivePatients = editForm.maxActivePatients;
      }
      await updateUser(selectedUser._id, updateData);
      // Update healer visibility separately
      await updateHealerVisibility(selectedUser._id, { showHealerNameToPatient: editForm.showHealerNameToPatient });
      setShowModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      alert('Error updating user');
    }
  };

  const handleImpersonate = async (user) => {
    try {
      const res = await impersonateUser(user._id);
      const { token, user: targetUser } = res.data;
      const url = window.location.origin + '/impersonate?t=' + encodeURIComponent(token) + '&role=' + targetUser.role + '&name=' + encodeURIComponent(targetUser.firstName);
      window.open(url, '_blank');
    } catch (e) {
      alert(e.response ? e.response.data.message : 'Could not impersonate user');
    }
  };

  const handleDelete = async (user) => {
    if (user.role === 'admin') {
      alert('Cannot delete admin users.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete ' + user.firstName + ' ' + user.lastName + '?\n\nThis action cannot be undone.')) return;
    try {
      await deleteUser(user._id);
      alert('User deleted successfully.');
      fetchUsers();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error deleting user');
    }
  };

  const roleColors = {
    admin: { bg: '#fdf0f0', color: '#e74c3c' },
    healer: { bg: '#fef9e7', color: '#d4ac0d' },
    student: { bg: '#eafaf1', color: '#27ae60' },
    patient: { bg: '#eaf4fb', color: '#2980b9' },
    general: { bg: '#f0f0f0', color: '#666' }
  };

  const roles = ['all', 'admin', 'healer', 'student', 'patient', 'general'];

  var exportUsersCSV = function() {
    var rows = [['Name', 'Email', 'Phone', 'Role', 'Active', 'Joined']];
    users.forEach(function(u) {
      rows.push([
        u.firstName + ' ' + (u.lastName || ''),
        u.email || '',
        u.phone || '',
        u.role || '',
        u.isActive ? 'Yes' : 'No',
        u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-IN') : ''
      ]);
    });
    var csv = rows.map(function(row) {
      return row.map(function(cell) { return '"' + String(cell).replace(/"/g, '""') + '"'; }).join(',');
    }).join('\n');
    var blob = new Blob([csv], { type: 'text/csv' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = 'users-' + new Date().toISOString().split('T')[0] + '.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  var s = Object.assign({}, styles, isMobile ? {
    filterBar: { ...styles.filterBar, flexDirection: 'column', alignItems: 'stretch', gap: '12px' },
    tabs: { ...styles.tabs, overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' },
    searchForm: { ...styles.searchForm, width: '100%' },
    searchInput: { ...styles.searchInput, width: '100%', flex: 1 },
    modal: { ...styles.modal, alignItems: 'flex-end', padding: '0' },
    modalCard: { ...styles.modalCard, borderRadius: '16px 16px 0 0', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' },
    modalBtns: { ...styles.modalBtns, flexDirection: 'column' },
    submitBtn: { ...styles.submitBtn, width: '100%' },
    cancelBtn: { ...styles.cancelBtn, width: '100%' }
  } : {});

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={{ ...styles.main, marginLeft: isMobile ? 0 : '260px', padding: isMobile ? '56px 16px 16px' : '32px' }}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.headerTitle}>User Management</h1>
            <p style={styles.headerSub}>Manage all registered members by role</p>
          </div>
          <button onClick={exportUsersCSV} style={{ background: 'transparent', color: '#4a8f82', border: '1px solid #4a8f82', borderRadius: '6px', padding: '10px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' }}>
            Export CSV
          </button>
        </div>

        {/* Filter + Search */}
        <div style={s.filterBar}>
          <div style={s.tabs}>
            {roles.map(role => (
              <button
                key={role}
                onClick={() => setFilter(role)}
                style={{
                  ...styles.tab,
                  ...(filter === role ? styles.tabActive : {})
                }}
              >
                {role.toUpperCase()}
              </button>
            ))}
          </div>
          <form onSubmit={handleSearch} style={s.searchForm}>
            <input
              style={s.searchInput}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email or phone..."
            />
            <button type="submit" style={styles.searchBtn}>Search</button>
          </form>
        </div>

        {/* Edit Modal */}
        {showModal && selectedUser && (
          <div style={s.modal}>
            <div style={s.modalCard}>
              <div style={styles.modalTitle}>Edit User</div>
              <div style={styles.modalInfo}>
                <div style={styles.modalName}>
                  {selectedUser.firstName} {selectedUser.lastName}
                </div>
                <div style={styles.modalEmail}>{selectedUser.email}</div>
                <div style={styles.modalPhone}>{selectedUser.phone}</div>
              </div>
              <form onSubmit={handleUpdate}>
                <div style={styles.field}>
                  <label style={styles.label}>User Role</label>
                  <select
                    style={styles.input}
                    value={editForm.role}
                    onChange={e => setEditForm({ ...editForm, role: e.target.value })}
                  >
                    <option value="general">PWC General User</option>
                    <option value="patient">PWC Patient</option>
                    <option value="student">PWC Student</option>
                    <option value="healer">PWC Healer</option>
                    <option value="admin">PWC Admin</option>
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Phone Number</label>
                  <input
                    style={styles.input}
                    value={editForm.phone}
                    onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                    placeholder="e.g. +919176880807"
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Account Status</label>
                  <select
                    style={styles.input}
                    value={editForm.isActive}
                    onChange={e => setEditForm({ ...editForm, isActive: e.target.value === 'true' })}
                  >
                    <option value="true">Active</option>
                    <option value="false">Deactivated</option>
                  </select>
                </div>
                {/* Healer Name Visibility — for patient/general */}
                {(editForm.role === 'patient' || editForm.role === 'general') && (
                  <div style={styles.field}>
                    <label style={styles.label}>Show Healer Name in Patient History</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                      <button type="button"
                        onClick={function() { setEditForm({ ...editForm, showHealerNameToPatient: !editForm.showHealerNameToPatient }); }}
                        style={{
                          width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', position: 'relative',
                          background: editForm.showHealerNameToPatient ? '#4a8f82' : '#ccc', transition: 'background 0.2s'
                        }}>
                        <div style={{
                          width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                          position: 'absolute', top: '2px',
                          left: editForm.showHealerNameToPatient ? '22px' : '2px', transition: 'left 0.2s'
                        }}></div>
                      </button>
                      <span style={{ fontSize: '13px', color: editForm.showHealerNameToPatient ? '#4a8f82' : '#999', fontWeight: '600' }}>
                        {editForm.showHealerNameToPatient ? 'ON — Healer name visible' : 'OFF — Shows as "Healer"'}
                      </span>
                    </div>
                  </div>
                )}
                {/* Healer capacity limit */}
                {(editForm.role === 'healer' || selectedUser.role === 'healer') && (
                  <div style={styles.field}>
                    <label style={styles.label}>Max Active Patients (Capacity Limit)</label>
                    <input style={styles.input} type="number" min="1" max="50"
                      value={editForm.maxActivePatients}
                      onChange={function(e) { setEditForm({ ...editForm, maxActivePatients: e.target.value }); }}
                      placeholder="Leave blank for unlimited" />
                    <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>Admin sees a warning when assigning beyond this limit.</div>
                  </div>
                )}
                {/* Admin notes about this user */}
                <div style={styles.field}>
                  <label style={styles.label}>Admin Notes (Internal Only)</label>
                  <textarea style={{ ...styles.input, height: '80px', resize: 'vertical' }}
                    value={editForm.profileNotes}
                    onChange={function(e) { setEditForm({ ...editForm, profileNotes: e.target.value }); }}
                    placeholder="e.g. Prefers morning sessions, has latex allergy, referred by Dr. XYZ..." />
                </div>
                {/* Allow Session View — for healer */}
                {editForm.role === 'healer' && (
                  <div style={styles.field}>
                    <label style={styles.label}>Allow Session View (In-house Healers Only)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                      <button type="button"
                        onClick={function() { setEditForm({ ...editForm, allowSessionView: !editForm.allowSessionView }); }}
                        style={{
                          width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', position: 'relative',
                          background: editForm.allowSessionView ? '#4a8f82' : '#ccc', transition: 'background 0.2s'
                        }}>
                        <div style={{
                          width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                          position: 'absolute', top: '2px',
                          left: editForm.allowSessionView ? '22px' : '2px', transition: 'left 0.2s'
                        }}></div>
                      </button>
                      <span style={{ fontSize: '13px', color: editForm.allowSessionView ? '#4a8f82' : '#999', fontWeight: '600' }}>
                        {editForm.allowSessionView ? 'ON — Can view upcoming sessions' : 'OFF — Healer Dashboard only'}
                      </span>
                    </div>
                  </div>
                )}
                <div style={s.modalBtns}>
                  <button type="submit" style={s.submitBtn}>Save Changes</button>
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); setSelectedUser(null); }}
                    style={s.cancelBtn}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.cardTitle}>
              {filter === 'all' ? 'All Users' : filter} ({users.length})
            </div>
          </div>
          {loading ? (
            <div style={styles.empty}>Loading users...</div>
          ) : users.length === 0 ? (
            <div style={styles.empty}>No users found.</div>
          ) : isMobile ? (
            /* Mobile Card Layout */
            <div style={{ padding: '0' }}>
              {users.map(function(user) {
                return (
                  <div key={user._id} style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div style={{ ...styles.avatar, background: roleColors[user.role]?.color || '#4a8f82' }}>
                        {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginBottom: '2px' }}>
                          {user.firstName} {user.lastName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>{user.email}</div>
                        {user.phone && <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>{user.phone}</div>}
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{ ...styles.badge, background: roleColors[user.role]?.bg, color: roleColors[user.role]?.color }}>{user.role}</span>
                          <span style={{ ...styles.badge, background: user.isActive ? '#eafaf1' : '#fdf0f0', color: user.isActive ? '#27ae60' : '#e74c3c' }}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {user.lastLoginAt && (
                            <span style={{ fontSize: '10px', color: '#999' }}>
                              Last: {new Date(user.lastLoginAt).toLocaleDateString('en-IN')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <button onClick={function() { handleViewProfile(user); }} style={{ ...styles.actionBtn, borderColor: '#2980b9', color: '#2980b9' }}>View</button>
                      <button onClick={function() { handleEdit(user); }} style={styles.actionBtn}>Edit</button>
                      {user.role !== 'admin' && (
                        <button onClick={function() { handleImpersonate(user); }} style={{ ...styles.actionBtn, borderColor: '#8e44ad', color: '#8e44ad' }} title={'Debug as ' + user.firstName}>Login as</button>
                      )}
                      {user.phone && (
                        <a href={'https://wa.me/' + user.phone.replace(/[^0-9]/g, '') + '?text=' + encodeURIComponent('Hello ' + (user.firstName || '') + ', this is Pranic Wellness Centre.')}
                          target="_blank" rel="noreferrer" style={styles.whatsappBtn} title={'WhatsApp ' + user.firstName}>
                          <FaWhatsapp style={{ fontSize: '14px' }} />
                        </a>
                      )}
                      {user.role !== 'admin' && (
                        <button onClick={function() { handleDelete(user); }} style={styles.deleteBtn} title={'Delete ' + user.firstName}>
                          <FiTrash2 style={{ fontSize: '14px' }} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop Table Layout */
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHead}>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Joined</th>
                  <th style={styles.th}>Last Login</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user._id} style={styles.tableRow}>
                    <td style={styles.td}>
                      <div style={styles.userCell}>
                        <div style={{
                          ...styles.avatar,
                          background: roleColors[user.role]?.color || '#4a8f82'
                        }}>
                          {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                        </div>
                        <div>
                          <div style={styles.tdTitle}>
                            {user.firstName} {user.lastName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>{user.email}</td>
                    <td style={styles.td}>{user.phone}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        background: roleColors[user.role]?.bg,
                        color: roleColors[user.role]?.color
                      }}>
                        {user.role}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        background: user.isActive ? '#eafaf1' : '#fdf0f0',
                        color: user.isActive ? '#27ae60' : '#e74c3c'
                      }}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {new Date(user.createdAt).toLocaleDateString('en-IN')}
                    </td>
                    <td style={styles.td}>
                      {user.lastLoginAt ? (
                        <span style={{ fontSize: '12px', color: '#333' }}>
                          {new Date(user.lastLoginAt).toLocaleDateString('en-IN')}{' '}
                          <span style={{ color: '#999' }}>{new Date(user.lastLoginAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                        </span>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#ccc' }}>Never</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button onClick={function() { handleViewProfile(user); }} style={{ ...styles.actionBtn, borderColor: '#2980b9', color: '#2980b9' }}>View</button>
                        <button
                          onClick={() => handleEdit(user)}
                          style={styles.actionBtn}
                        >
                          Edit
                        </button>
                        {user.role !== 'admin' && (
                          <button
                            onClick={function() { handleImpersonate(user); }}
                            style={{ ...styles.actionBtn, borderColor: '#8e44ad', color: '#8e44ad' }}
                            title={'Debug as ' + user.firstName}
                          >
                            Login as
                          </button>
                        )}
                        {user.phone && (
                          <a
                            href={'https://wa.me/' + user.phone.replace(/[^0-9]/g, '') + '?text=' + encodeURIComponent('Hello ' + (user.firstName || '') + ', this is Pranic Wellness Centre.')}
                            target="_blank"
                            rel="noreferrer"
                            style={styles.whatsappBtn}
                            title={'WhatsApp ' + user.firstName}
                          >
                            <FaWhatsapp style={{ fontSize: '14px' }} />
                          </a>
                        )}
                        {user.role !== 'admin' && (
                          <button
                            onClick={() => handleDelete(user)}
                            style={styles.deleteBtn}
                            title={'Delete ' + user.firstName}
                          >
                            <FiTrash2 style={{ fontSize: '14px' }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      {/* ── View Profile Modal (#14) ── */}
      {profileModal && viewProfileUser && (
        <div style={styles.modal} onClick={function(e) { if (e.target === e.currentTarget) setProfileModal(false); }}>
          <div style={{ ...styles.modalCard, maxWidth: '640px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={styles.modalTitle}>{viewProfileUser.firstName} {viewProfileUser.lastName}</div>
              <button onClick={function() { setProfileModal(false); }} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#999' }}>✕</button>
            </div>

            {/* User summary */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              {[
                { label: 'Email', value: viewProfileUser.email },
                { label: 'Phone', value: viewProfileUser.phone || '—' },
                { label: 'Role', value: viewProfileUser.role?.toUpperCase() },
                { label: 'Status', value: viewProfileUser.isActive ? 'Active' : 'Deactivated' },
                { label: 'Joined', value: viewProfileUser.createdAt ? new Date(viewProfileUser.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—' },
                { label: 'Last Login', value: viewProfileUser.lastLoginAt ? new Date(viewProfileUser.lastLoginAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Never' },
              ].map(function(item) {
                return (
                  <div key={item.label} style={{ background: '#f9faf7', borderRadius: '6px', padding: '10px 14px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '3px' }}>{item.label}</div>
                    <div style={{ fontSize: '13px', color: '#1a1a2e', fontWeight: '600' }}>{item.value}</div>
                  </div>
                );
              })}
            </div>

            {/* Admin notes */}
            {viewProfileUser.profileNotes && (
              <div style={{ background: '#fef9e7', border: '1px solid #d4ac0d', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: '#7d6608', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '5px' }}>Admin Notes</div>
                <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{viewProfileUser.profileNotes}</div>
              </div>
            )}

            {/* Healing cases */}
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px' }}>
              {viewProfileUser.role === 'healer' ? 'Assigned Cases' : 'Healing Requests'} ({profileRequests.length})
            </div>
            {profileLoading ? (
              <div style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: '13px' }}>Loading...</div>
            ) : profileRequests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: '#bbb', fontSize: '13px' }}>No healing requests found.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {profileRequests.map(function(req) {
                  var statusColor = { pending: '#d4ac0d', assigned: '#2980b9', in_progress: '#27ae60', completed: '#666', cancelled: '#e74c3c' };
                  var patientName = req.patient ? req.patient.firstName + ' ' + req.patient.lastName : (req.manualPatientName || '—');
                  var healerName = req.assignedHealer ? req.assignedHealer.firstName + ' ' + req.assignedHealer.lastName : 'Unassigned';
                  return (
                    <div key={req._id} style={{ background: '#f9faf7', borderRadius: '8px', padding: '12px 16px', border: '1px solid #e8f0e8' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e' }}>{req.condition}</div>
                          <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                            {viewProfileUser.role === 'healer' ? 'Patient: ' + patientName : 'Healer: ' + healerName}
                            &nbsp;·&nbsp;{req.totalSessionsCompleted} sessions
                            &nbsp;·&nbsp;{new Date(req.requestedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                        </div>
                        <span style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '12px', fontWeight: '700', background: '#f0f0f0', color: statusColor[req.status] || '#666', whiteSpace: 'nowrap' }}>
                          {req.status?.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: '24px', display: 'flex', gap: '10px' }}>
              <button onClick={function() { setProfileModal(false); handleEdit(viewProfileUser); }} style={styles.actionBtn}>Edit User</button>
              <button onClick={function() { setProfileModal(false); }} style={styles.cancelBtn}>Close</button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif" },
  main: { marginLeft: '260px', flex: 1, padding: '32px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  headerTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '26px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 4px' },
  headerSub: { fontSize: '13px', color: '#666', margin: 0 },
  filterBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', gap: '16px' },
  tabs: { display: 'flex', gap: '4px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px' },
  tab: { padding: '8px 16px', background: 'transparent', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', color: '#666', cursor: 'pointer' },
  tabActive: { background: '#4a8f82', color: '#fff' },
  searchForm: { display: 'flex', gap: '8px' },
  searchInput: { padding: '10px 16px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '13px', width: '280px', fontFamily: "'Open Sans', sans-serif" },
  searchBtn: { padding: '10px 20px', background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '1px' },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' },
  modalCard: { background: '#fff', borderRadius: '10px', padding: '36px', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '20px', fontWeight: '700', color: '#1a1a2e', marginBottom: '16px' },
  modalInfo: { background: '#f9faf7', borderRadius: '8px', padding: '16px', marginBottom: '24px' },
  modalName: { fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
  modalEmail: { fontSize: '13px', color: '#4a8f82', marginBottom: '2px' },
  modalPhone: { fontSize: '13px', color: '#666' },
  modalBtns: { display: 'flex', gap: '12px', marginTop: '8px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666', marginBottom: '6px', fontFamily: "'Raleway', sans-serif" },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '5px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333' },
  submitBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '12px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  cancelBtn: { background: 'transparent', color: '#666', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '12px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #e0e0e0' },
  cardTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e' },
  empty: { padding: '40px', textAlign: 'center', color: '#999', fontSize: '14px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHead: { background: '#f9faf7' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#666', fontFamily: "'Raleway', sans-serif", borderBottom: '1px solid #e0e0e0' },
  tableRow: { borderBottom: '1px solid #f5f5f5' },
  td: { padding: '14px 16px', fontSize: '13px', color: '#333', verticalAlign: 'middle' },
  tdTitle: { fontFamily: "'Raleway', sans-serif", fontWeight: '700', color: '#1a1a2e' },
  userCell: { display: 'flex', alignItems: 'center', gap: '12px' },
  avatar: { width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#fff', flexShrink: 0 },
  badge: { fontFamily: "'Raleway', sans-serif", fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', padding: '4px 10px', borderRadius: '20px' },
  actionBtn: { background: 'none', border: '1px solid #4a8f82', color: '#4a8f82', borderRadius: '4px', padding: '6px 12px', fontSize: '11px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px' },
  whatsappBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', background: '#25D366', color: '#fff', borderRadius: '50%', textDecoration: 'none', flexShrink: 0, transition: 'opacity 0.2s' },
  deleteBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', background: 'transparent', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: '50%', cursor: 'pointer', flexShrink: 0, transition: 'opacity 0.2s' }
};

export default Users;